import React, {type ChangeEventHandler, useCallback, useEffect, useMemo, useState} from 'react';


import {GroupsList} from 'src/components/group-suggest/groups-list';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getAccountGroups} from 'src/openapi/admin/apis/GetAccountGroupsApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';

import type {AccountGroup} from 'src/openapi/admin/models/AccountGroup';
import type {ServerResponse} from 'src/typedef';

const MAX_RESULTS = 50;

type Props = {
    id: string,
    accountId: number,
    selectedGroupId?: number | null,
    onSelect: (group: AccountGroup | null) => void,
    placeholder: string,
    width: number | string,
    disabled?: boolean,
    onFocus?: () => void,
    onChangeQuery?: (query: string) => void,
};

export const GroupSuggest: React.FC<Props> = (props: Props) => {
    const {id, accountId, selectedGroupId, onSelect, width, disabled, placeholder, onFocus, onChangeQuery} = props;
    const apiDispatch = useAppDispatch();

    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [invalid, setInvalid] = useState(false);

    const [allGroupsResponse, setAllGroupsResponse] = useState<ServerResponse<Array<AccountGroup>> | null>(null);
    const [response, setResponse] = useState<ServerResponse<Array<AccountGroup>> | null>(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const loadAccountGroups = async () => {
            setBusy(true);
            const accountGroupsResponse = await apiDispatch(getAccountGroups({accountId}));

            if (accountGroupsResponse.payload.success) {
                setAllGroupsResponse({data: accountGroupsResponse.payload.data});

                if (selectedGroupId) {
                    const selectedGroup = accountGroupsResponse.payload.data
                        .find(group => group.id === selectedGroupId);

                    setInvalid(!selectedGroup);
                    setQuery(selectedGroup ? selectedGroup.name : 'Invalid group');
                }
            } else {
                setAllGroupsResponse({error: {message: 'Can\'t suggest groups. Service is temporary unavailable.'}});
            }
            setBusy(false);
        };

        loadAccountGroups();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({
            prev: prev.selectedGroupId,
            current: current.selectedGroupId,
        }) && current.allGroupsResponse?.data) {
            if (selectedGroupId) {
                const selectedGroup = current.allGroupsResponse?.data
                    .find(group => group.id === selectedGroupId);

                setQuery(selectedGroup ? selectedGroup.name : 'Invalid group');
                setInvalid(!selectedGroup);
            } else {
                setQuery('');
                setInvalid(false);
            }
        }
    }, {selectedGroupId, allGroupsResponse});

    const getSuggestions = useCallback((newValue: string) => {
        const preparedQuery = newValue.toLowerCase().trim();
        if (allGroupsResponse?.data) {
            const groups = allGroupsResponse.data.filter(({name}) =>
                name.toLowerCase().includes(preparedQuery),
            ).slice(0, MAX_RESULTS);

            setResponse({data: groups});
        } else {
            setResponse({error: {message: 'Can\'t suggest template users. Service is temporary unavailable.'}});
        }
    }, [allGroupsResponse]);

    const onChangeQueryHandler = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setQuery(value);
        getSuggestions(value);
        if (onChangeQuery) {
            onChangeQuery(value);
        }
    }, [getSuggestions, onChangeQuery]);

    const clearSuggest = useCallback(() => {
        setQuery('');
        onSelect(null);
        setResponse(null);
    }, [onSelect]);

    const onItemSelect = useCallback((group: AccountGroup) => {
        onSelect(group);
        setQuery(group.name);
        setResponse(null);
    }, [onSelect]);

    const onClick = useCallback(() => {
        setShown(true);
        getSuggestions(query);
    }, [getSuggestions, query]);

    const noGroups = useMemo(() => {
        return !!(allGroupsResponse?.data && !allGroupsResponse.data.length);
    }, [allGroupsResponse]);

    return (
        <div style={{width}}>
            <Autosuggest
                color={invalid ? 'ERROR' : undefined}
                id={id}
                value={query}
                shown={shown}
                setShown={setShown}
                onClick={onClick}
                onChange={onChangeQueryHandler}
                onFocus={onFocus}
                disabled={disabled || noGroups}
                placeholder={noGroups
                    ? 'No groups have been created in this account'
                    : placeholder}
                onItemSelect={onItemSelect}
                onClear={clearSuggest}>
                <GroupsList id={`${id}-list`} busy={busy} response={response}/>
            </Autosuggest>
        </div>
    );
};
